import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Input, Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { usePlaygroundStore } from "@/stores/PlaygroundStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useFileUpload } from "@/hooks/chat/useFileUpload";
import { FileUploadProgress } from "@/components/Chat/FileUploadProgress";
import { createStructuredUploadError } from "@/utils/chat/validationHelpers";

export const InputPropertiesPanel: React.FC = () => {
  const { t } = useTranslation();
  const { workflow, setWorkflow } = useWorkflowStore();
  const { uploadedFiles, setUploadedFiles } = usePlaygroundStore();
  const { platformClient } = useWebuildAPIClient();

  const inputParams = workflow?.input || {};

  // Only show file upload when workflow has at least one chat_agent
  const hasChatAgent = useMemo(() => {
    if (!workflow?.agents) return false;

    return Object.values(workflow.agents).some((a) => a.type === "chat_agent");
  }, [workflow]);

  // Abort controller ref to cancel polling when component unmounts
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const {
    files,
    uploadProgress,
    isUploading,
    failedFiles,
    fileInputRef,
    handleFileChange,
    removeFile,
    retryFailedUploads,
  } = useFileUpload({
    onUpload: useCallback(
      async (filesToUpload: File[]) => {
        if (!platformClient) throw new Error("Platform client not available");

        const controller = new AbortController();

        abortRef.current = controller;

        const uploaded = await Promise.all(
          filesToUpload.map(async (file) => {
            if (controller.signal.aborted) throw new Error("Upload cancelled");

            const response = await platformClient.files.upload({
              bodyUploadFileBackgroundPlatformV1DataFilesPost: {
                file: file,
                summary: file.name,
                tags: [],
                fileType: file.type,
                chatId: "playground",
                private: true,
                parser: "llm",
              },
            });

            if (!response) {
              throw new Error(`Upload failed for ${file.name}`);
            }

            // Poll job status until processing completes
            let jobStatus = await platformClient.files.getJobStatus({
              jobId: response.job_id,
            });

            while (jobStatus?.status === "pending") {
              if (controller.signal.aborted) throw new Error("Upload cancelled");
              await new Promise((resolve) => setTimeout(resolve, 1000));
              if (controller.signal.aborted) throw new Error("Upload cancelled");
              jobStatus = await platformClient.files.getJobStatus({
                jobId: response.job_id,
              });
            }

            if (jobStatus?.status === "failed") {
              const errorMessage =
                jobStatus.result ||
                t("fileUpload.errors.generic.message", { defaultValue: "File upload failed" });

              throw createStructuredUploadError(errorMessage, jobStatus);
            }

            return {
              name: file.name,
              size: String(file.size),
              type: file.type,
              id: jobStatus?.result,
              status: t("Completed"),
            };
          }),
        );

        return uploaded;
      },
      [platformClient, t],
    ),
    maxFiles: 10,
  });

  // Sync completed files to PlaygroundStore
  useEffect(() => {
    const completedFiles = files.filter((f) => f.id && !f.id.startsWith("uploading-"));

    if (completedFiles.length > 0) {
      setUploadedFiles(completedFiles);
    }
  }, [files, setUploadedFiles]);

  // Merge store files (persistent across remounts) with in-progress uploads from the hook
  const displayFiles = useMemo(() => {
    const inProgressFiles = files.filter((f) => f.id?.startsWith("uploading-"));

    // Avoid duplicates: store files + any in-progress uploads not yet in store
    const storeFileNames = new Set(uploadedFiles.map((f) => f.name));
    const uniqueInProgress = inProgressFiles.filter((f) => !storeFileNames.has(f.name));

    return [...uploadedFiles, ...uniqueInProgress];
  }, [uploadedFiles, files]);

  const handleRemoveFile = useCallback(
    (fileName: string) => {
      // Remove from hook state (in case it's in-progress)
      removeFile(fileName);
      // Remove from store (for completed files)
      setUploadedFiles(uploadedFiles.filter((f) => f.name !== fileName));
    },
    [removeFile, uploadedFiles, setUploadedFiles],
  );

  const updateInputParam = (paramName: string, field: string, value: string) => {
    if (!workflow) return;

    const updatedInput = {
      ...workflow.input,
      [paramName]: {
        ...(workflow.input?.[paramName] || {}),
        [field]: value,
      },
    };

    setWorkflow({ ...workflow, input: updatedInput });
  };

  const addInputParam = () => {
    if (!workflow) return;

    // First param defaults to "prompt" (the standard input key); subsequent ones get numbered names
    const paramName = !inputParams.prompt
      ? "prompt"
      : `param_${Object.keys(inputParams).length + 1}`;
    const updatedInput = {
      ...workflow.input,
      [paramName]: {
        type: "string",
        description: "",
      },
    };

    setWorkflow({ ...workflow, input: updatedInput });
  };

  const removeInputParam = (paramName: string) => {
    if (!workflow) return;

    const updatedInput = { ...workflow.input };

    delete updatedInput[paramName];
    setWorkflow({ ...workflow, input: updatedInput });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-stone-500" icon="solar:document-text-linear" />
          <h3 className="text-sm font-semibold">{t("Input Configuration")}</h3>
        </div>
        <Button
          isIconOnly
          aria-label={t("Add parameter")}
          size="sm"
          variant="flat"
          onPress={addInputParam}
        >
          <Icon className="w-4 h-4" icon="solar:add-circle-linear" />
        </Button>
      </div>

      {Object.keys(inputParams).length === 0 ? (
        <p className="text-xs text-default-400 text-center py-4">
          {t("No input parameters defined")}
        </p>
      ) : (
        Object.entries(inputParams).map(([paramName, paramValue]: [string, any]) => (
          <div key={paramName} className="p-3 rounded-lg bg-default-50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-default-700">{paramName}</span>
              <Button
                isIconOnly
                aria-label={t("Remove parameter")}
                size="sm"
                variant="light"
                onPress={() => removeInputParam(paramName)}
              >
                <Icon className="w-3.5 h-3.5 text-danger" icon="solar:close-circle-linear" />
              </Button>
            </div>
            <Input
              label={t("Type")}
              size="sm"
              value={paramValue?.type || "string"}
              onValueChange={(value) => updateInputParam(paramName, "type", value)}
            />
            <Input
              label={t("Description")}
              size="sm"
              value={paramValue?.description || ""}
              onValueChange={(value) => updateInputParam(paramName, "description", value)}
            />
            <Input
              label={t("Default Value")}
              placeholder={t("Optional")}
              size="sm"
              value={paramValue?.value || ""}
              onValueChange={(value) => updateInputParam(paramName, "value", value)}
            />
          </div>
        ))
      )}

      {/* File Upload — only for workflows with chat_agent */}
      {hasChatAgent && (
        <div className="pt-2 border-t border-default-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-default-500" icon="solar:file-send-linear" />
              <label className="text-xs font-semibold text-default-700">{t("Files")}</label>
            </div>
            <Button
              isIconOnly
              aria-label={t("Upload files")}
              isDisabled={isUploading}
              size="sm"
              variant="flat"
              onPress={() => fileInputRef.current?.click()}
            >
              <Icon className="w-4 h-4" icon="solar:upload-minimalistic-linear" />
            </Button>
            <input
              ref={fileInputRef}
              multiple
              accept=".pdf,.doc,.docx,.pptx,.png,.jpg,.jpeg,.svg,.csv,.xlsx,.xls,.txt,.md,.json,.yaml,.yml,.xml"
              className="hidden"
              type="file"
              onChange={handleFileChange}
            />
          </div>

          {displayFiles.length === 0 && !isUploading ? (
            <p className="text-xs text-default-400 text-center py-2">
              {t("No files uploaded. Click + to attach files.")}
            </p>
          ) : (
            <FileUploadProgress
              files={displayFiles}
              showRetryButton={failedFiles.length > 0}
              uploadProgress={uploadProgress}
              onRemoveFile={handleRemoveFile}
              onRetry={retryFailedUploads}
            />
          )}
        </div>
      )}
    </div>
  );
};
